'use client';
import { FormEvent, useEffect, useState } from 'react';
import { Navigate } from 'react-router';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import {
  requestEmailConfirmation,
  updateEmailCommunicationPreference,
  updateWebPassword,
} from '../../auth';
import {
  createUserAddress,
  deleteUserAddress,
  fetchUserAddresses,
  setDefaultUserAddress,
  updateUserAddress,
} from '../../api';
import { useWebsiteAppContext } from '../../hooks/useWebsiteAppContext';
import { getLocalizedApiErrorMessage } from '../../utils/errorHelpers';
import type { DeliveryAddress, OrderLocation } from '../../types';
import { useTranslation } from 'react-i18next';
import AddressAutocomplete from '../../components/AddressAutocomplete';
import TelegramLinkPanel from '../../components/TelegramLinkPanel';
import AddressMapModal from '../../../miniapp/components/AddressMapModal';
import { BrowserOnly } from '../../client/BrowserOnly';
import { LoadingState } from '../../../miniapp/ui-kit/Spinner';
import Icon from '../../../miniapp/ui-kit/Icon';

export default function ProfileScreen() {
  const { t, i18n } = useTranslation();
  const {
    sessionUser,
    appConfig,
    refreshSessionUser,
    setError,
    clearError,
    logout,
  } = useWebsiteAppContext();

  const [message, setMessage] = useState('');
  const [emailInput, setEmailInput] = useState(sessionUser?.email ?? '');
  const [passwordCurrent, setPasswordCurrent] = useState('');
  const [passwordNew, setPasswordNew] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [emailPending, setEmailPending] = useState(false);
  const [communicationPending, setCommunicationPending] = useState(false);
  const [passwordPending, setPasswordPending] = useState(false);
  const [addresses, setAddresses] = useState<DeliveryAddress[]>([]);
  const [addressesLoading, setAddressesLoading] = useState(false);
  const [addressPending, setAddressPending] = useState(false);
  const [addressFormOpen, setAddressFormOpen] = useState(false);
  const [editingAddressId, setEditingAddressId] = useState<number | null>(null);
  const [addressLabel, setAddressLabel] = useState('');
  const [addressValue, setAddressValue] = useState('');
  const [addressPhone, setAddressPhone] = useState('');
  const [addressLocation, setAddressLocation] = useState<OrderLocation | null>(null);
  const [mapTargetAddressId, setMapTargetAddressId] = useState<number | null>(null);

  useEffect(() => {
    setEmailInput(sessionUser?.email ?? '');
  }, [sessionUser?.email]);

  useEffect(() => {
    if (!sessionUser) {
      setAddresses([]);
      return;
    }

    let active = true;
    setAddressesLoading(true);
    fetchUserAddresses()
      .then((items) => {
        if (active) {
          setAddresses(items);
        }
      })
      .catch(() => {
        if (active) {
          setAddresses([]);
        }
      })
      .finally(() => {
        if (active) {
          setAddressesLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [sessionUser]);

  async function refreshAddresses(): Promise<void> {
    if (!sessionUser) {
      setAddresses([]);
      return;
    }
    const items = await fetchUserAddresses();
    setAddresses(items);
  }

  function resetAddressForm(): void {
    setEditingAddressId(null);
    setAddressLabel('');
    setAddressValue('');
    setAddressPhone('');
    setAddressLocation(null);
  }

  function openCreateAddressForm(): void {
    resetAddressForm();
    setAddressFormOpen(true);
  }

  function openEditAddressForm(address: DeliveryAddress): void {
    setEditingAddressId(address.id);
    setAddressLabel(address.label ?? '');
    setAddressValue(address.address);
    setAddressPhone(address.phoneNumber ?? '');
    setAddressLocation(address.location);
    setAddressFormOpen(true);
  }

  if (!sessionUser) {
    return <Navigate to="/login" replace />;
  }
  const authenticatedUser = sessionUser;

  async function handleEmailConfirmationRequest(event: FormEvent): Promise<void> {
    event.preventDefault();
    setEmailPending(true);
    setMessage('');
    clearError();
    try {
      const response = await requestEmailConfirmation(emailInput.trim());
      await refreshSessionUser();
      if (response.confirmationUrl) {
        setMessage(t('website.profile.confirmationSentWithLink', { url: response.confirmationUrl }));
      } else {
        setMessage(t('website.profile.confirmationSent'));
      }
    } catch (error) {
      setError(
        getLocalizedApiErrorMessage(error, t, {
          exists: i18n.exists.bind(i18n),
        }),
      );
    } finally {
      setEmailPending(false);
    }
  }

  async function handlePasswordUpdate(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (passwordNew !== passwordConfirm) {
      setError(t('settings.password.mismatch'));
      return;
    }

    setPasswordPending(true);
    setMessage('');
    clearError();
    try {
      const payload: { newPassword: string; currentPassword?: string } = {
        newPassword: passwordNew,
      };
      if (authenticatedUser.hasPassword) {
        payload.currentPassword = passwordCurrent;
      }
      await updateWebPassword(payload);
      await refreshSessionUser();
      setPasswordCurrent('');
      setPasswordNew('');
      setPasswordConfirm('');
      setMessage(t('settings.password.updated'));
    } catch (error) {
      setError(
        getLocalizedApiErrorMessage(error, t, {
          exists: i18n.exists.bind(i18n),
        }),
      );
    } finally {
      setPasswordPending(false);
    }
  }

  async function handleTelegramSessionChanged(linked: boolean): Promise<void> {
    await refreshSessionUser();
    if (linked) {
      clearError();
      setMessage(t('website.profile.telegramLinked'));
    }
  }

  async function handleEmailCommunicationToggle(nextAllowEmails: boolean): Promise<void> {
    setCommunicationPending(true);
    setMessage('');
    clearError();

    try {
      await updateEmailCommunicationPreference(nextAllowEmails);
      await refreshSessionUser();
      setMessage(
        nextAllowEmails
          ? t('website.profile.emailCommunicationEnabled')
          : t('website.profile.emailCommunicationDisabled'),
      );
    } catch (error) {
      setError(
        getLocalizedApiErrorMessage(error, t, {
          exists: i18n.exists.bind(i18n),
        }),
      );
    } finally {
      setCommunicationPending(false);
    }
  }

  async function handleSubmitAddress(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const normalizedAddress = addressValue.trim();
    if (!normalizedAddress) {
      setError(t('order.errors.deliveryAddressRequired'));
      return;
    }
    if (!addressLocation) {
      setError(t('order.errors.locationRequired'));
      return;
    }

    setAddressPending(true);
    setMessage('');
    clearError();
    try {
      const payload = {
        label: addressLabel.trim() || null,
        address: normalizedAddress,
        phoneNumber: addressPhone.trim() || null,
        location: addressLocation,
      };

      if (editingAddressId) {
        await updateUserAddress(editingAddressId, payload);
      } else {
        await createUserAddress(payload);
      }

      await refreshAddresses();
      setAddressFormOpen(false);
      resetAddressForm();
      setMessage(t('addresses.saveSuccess'));
    } catch (error) {
      setError(
        getLocalizedApiErrorMessage(error, t, {
          exists: i18n.exists.bind(i18n),
        }),
      );
    } finally {
      setAddressPending(false);
    }
  }

  async function handleDeleteAddress(address: DeliveryAddress): Promise<void> {
    if (!window.confirm(t('addresses.deleteConfirm'))) {
      return;
    }

    setAddressPending(true);
    setMessage('');
    clearError();
    try {
      await deleteUserAddress(address.id);
      await refreshAddresses();
      setMessage(t('addresses.deleteSuccess'));
    } catch (error) {
      setError(
        getLocalizedApiErrorMessage(error, t, {
          exists: i18n.exists.bind(i18n),
        }),
      );
    } finally {
      setAddressPending(false);
    }
  }

  async function handleSetDefaultAddress(address: DeliveryAddress): Promise<void> {
    if (address.isDefault) {
      return;
    }

    setAddressPending(true);
    setMessage('');
    clearError();
    try {
      await setDefaultUserAddress(address.id);
      await refreshAddresses();
    } catch (error) {
      setError(
        getLocalizedApiErrorMessage(error, t, {
          exists: i18n.exists.bind(i18n),
        }),
      );
    } finally {
      setAddressPending(false);
    }
  }

  async function handleSaveAddressLocation(location: OrderLocation): Promise<void> {
    if (!mapTargetAddressId) {
      return;
    }

    setAddressPending(true);
    setMessage('');
    clearError();
    try {
      await updateUserAddress(mapTargetAddressId, { location });
      await refreshAddresses();
      setMapTargetAddressId(null);
      setMessage(t('website.profile.addressLocationUpdated'));
    } catch (error) {
      setError(
        getLocalizedApiErrorMessage(error, t, {
          exists: i18n.exists.bind(i18n),
        }),
      );
    } finally {
      setAddressPending(false);
    }
  }

  const userDisplayName =
    [authenticatedUser.firstName, authenticatedUser.lastName].filter(Boolean).join(' ') || t('website.profile.memberFallback');
  const userHandle = authenticatedUser.username
    ? `@${authenticatedUser.username}`
    : (authenticatedUser.email?.split('@')[0] || t('website.profile.userFallback'));
  const mapTargetAddress = mapTargetAddressId
    ? addresses.find((address) => address.id === mapTargetAddressId) ?? null
    : null;

  return (
    <>
    <section className="max-w-[800px] mx-auto py-10 px-6">
      <header className="mb-10 text-center md:text-left">
        <h1 className="text-4xl font-bold editorial-title mb-2 uppercase tracking-tight">{t('website.profile.memberProfileTitle')}</h1>
        <p className="text-text-secondary text-sm md:text-base max-w-[600px]">
          {t('website.profile.memberProfileSubtitle')}
        </p>
      </header>

      <div className="grid gap-8">
        {/* Profile Header Card */}
        <div className="bg-primary rounded-4xl p-6 md:p-8 border border-border shadow-ambient flex flex-col md:flex-row items-center gap-6">
           <div className="w-24 h-24 rounded-circle bg-secondary flex items-center justify-center text-accent shrink-0 border border-border">
             <Icon name="person" className="text-5xl" />
           </div>
           <div className="flex-1 text-center md:text-left">
             <h2 className="text-2xl font-bold uppercase tracking-wide">{userDisplayName}</h2>
             <div className="flex items-center justify-center md:justify-start gap-2 text-text-secondary mt-1">
               <Icon name="alternate_email" className="text-sm" />
               <span className="text-sm">{userHandle}</span>
             </div>
           </div>
        </div>

        {/* Telegram Sync */}
        <div className="bg-primary rounded-4xl border border-border overflow-hidden shadow-sm">
           <div className="px-6 py-4 border-b border-border bg-secondary/20 flex items-center justify-between">
              <div>
                <h3 className="text-xs font-bold uppercase tracking-[0.2em] mb-0.5">{t('website.profile.telegramSyncTitle')}</h3>
                <p className="text-[10px] text-text-secondary uppercase tracking-wider">{t('website.profile.telegramSyncSubtitle')}</p>
              </div>
              <Icon name="sync" className="text-text-secondary opacity-30 text-xl" />
           </div>
           <div className="p-6">
             {authenticatedUser.telegramId ? (
               <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Icon name="verified" className="text-success" />
                    <span className="font-medium">
                      {authenticatedUser.username ? `@${authenticatedUser.username}` : userDisplayName}
                    </span>
                  </div>
                  <button onClick={() => void refreshSessionUser()} className="text-xs font-bold uppercase tracking-widest text-accent hover:underline">
                    {t('website.profile.refreshStatus')}
                  </button>
               </div>
             ) : (
               <BrowserOnly reason="Telegram deep-link polling uses window and timers" fallback={<LoadingState />}>
                 <TelegramLinkPanel onSessionChanged={handleTelegramSessionChanged} />
               </BrowserOnly>
             )}
           </div>
        </div>

        {/* Email Authentication */}
        <div className="bg-primary rounded-4xl border border-border overflow-hidden shadow-sm">
           <div className="px-6 py-4 border-b border-border bg-secondary/20 flex items-center justify-between">
              <div>
                <h3 className="text-xs font-bold uppercase tracking-[0.2em] mb-0.5">{t('website.profile.emailAuthTitle')}</h3>
                <p className="text-[10px] text-text-secondary uppercase tracking-wider">{t('website.profile.emailAuthSubtitle')}</p>
              </div>
              <Icon name="mail" className="text-text-secondary opacity-30 text-xl" />
           </div>
           <div className="p-6">
             <form className="flex flex-col md:flex-row gap-3" onSubmit={(event) => void handleEmailConfirmationRequest(event)}>
                <div className="relative flex-1">
                  <Icon
                    name="mail"
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary text-lg"
                  />
                  <input
                    type="email"
                    className="w-full border border-border-solid rounded-2xl pl-10 pr-4 py-2.5 bg-secondary/30 text-text placeholder:text-text-secondary/50 focus:outline-none focus:ring-2 focus:ring-accent/20"
                    value={emailInput}
                    onChange={(event) => setEmailInput(event.target.value)}
                    placeholder={t('settings.email.placeholder')}
                    required
                  />
                  {authenticatedUser.emailConfirmed && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1 text-[10px] font-bold text-success uppercase tracking-wider">
                      <Icon name="verified" className="text-sm" />
                      {t('settings.email.confirmed')}
                    </span>
                  )}
                </div>
                <button
                  type="submit"
                  className="bg-secondary text-text px-6 py-2.5 rounded-2xl font-bold text-xs uppercase tracking-widest hover:bg-surface-high transition-colors disabled:opacity-50"
                  disabled={emailPending}
                >
                  {emailPending ? t('website.profile.sending') : authenticatedUser.emailConfirmed ? t('website.profile.change') : t('website.profile.confirm')}
                </button>
             </form>
           </div>
        </div>

        {/* Security & Access */}
        {authenticatedUser.email && authenticatedUser.emailConfirmed && authenticatedUser.telegramId && (
          <div className="bg-primary rounded-4xl border border-border overflow-hidden shadow-sm">
             <div className="px-6 py-4 border-b border-border bg-secondary/20 flex items-center justify-between">
                <div>
                  <h3 className="text-xs font-bold uppercase tracking-[0.2em] mb-0.5">{t('settings.communication.title')}</h3>
                  <p className="text-[10px] text-text-secondary uppercase tracking-wider">{t('settings.communication.hint')}</p>
                </div>
                <Icon name="notifications" className="text-text-secondary opacity-30 text-xl" />
             </div>
             <div className="p-6 flex items-center justify-between gap-4">
               <div>
                 <p className="m-0 text-sm font-semibold uppercase tracking-wider">{t('settings.communication.allowEmail')}</p>
                 <p className="m-0 mt-1 text-xs text-text-secondary">{t('website.profile.communicationHint')}</p>
               </div>
               <button
                 type="button"
                 className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors border-none cursor-pointer ${authenticatedUser.allowEmails ? 'bg-success' : 'bg-disabled'}`}
                 onClick={() => void handleEmailCommunicationToggle(!authenticatedUser.allowEmails)}
                 disabled={communicationPending}
                 aria-pressed={authenticatedUser.allowEmails}
                 aria-label={t('settings.communication.allowEmail')}
               >
                 <span
                   className={`inline-block h-6 w-6 transform rounded-full bg-primary transition-transform ${authenticatedUser.allowEmails ? 'translate-x-7' : 'translate-x-1'}`}
                 />
               </button>
             </div>
          </div>
        )}

        {authenticatedUser.emailConfirmed && (
          <div className="bg-primary rounded-4xl border border-border overflow-hidden shadow-sm">
             <div className="px-6 py-4 border-b border-border bg-secondary/20 flex items-center justify-between">
                <div>
                  <h3 className="text-xs font-bold uppercase tracking-[0.2em] mb-0.5">{t('website.profile.securityTitle')}</h3>
                  <p className="text-[10px] text-text-secondary uppercase tracking-wider">{t('website.profile.securitySubtitle')}</p>
                </div>
                <Icon name="shield" className="text-text-secondary opacity-30 text-xl" />
             </div>
             <div className="p-6">
               <form className="grid gap-4" onSubmit={(event) => void handlePasswordUpdate(event)}>
                  <div className="grid md:grid-cols-3 gap-4">
                    {authenticatedUser.hasPassword && (
                      <input
                        type="password"
                        className="w-full border border-border-solid rounded-2xl px-4 py-2.5 bg-secondary/30 text-text placeholder:text-text-secondary/50 focus:outline-none focus:ring-2 focus:ring-accent/20"
                        value={passwordCurrent}
                        onChange={(event) => setPasswordCurrent(event.target.value)}
                        placeholder={t('settings.password.currentPassword')}
                        required
                      />
                    )}
                    <input
                      type="password"
                      className="w-full border border-border-solid rounded-2xl px-4 py-2.5 bg-secondary/30 text-text placeholder:text-text-secondary/50 focus:outline-none focus:ring-2 focus:ring-accent/20"
                      value={passwordNew}
                      onChange={(event) => setPasswordNew(event.target.value)}
                      placeholder={t('settings.password.newPassword')}
                      minLength={8}
                      required
                    />
                    <input
                      type="password"
                      className="w-full border border-border-solid rounded-2xl px-4 py-2.5 bg-secondary/30 text-text placeholder:text-text-secondary/50 focus:outline-none focus:ring-2 focus:ring-accent/20"
                      value={passwordConfirm}
                      onChange={(event) => setPasswordConfirm(event.target.value)}
                      placeholder={t('settings.password.confirmPassword')}
                      minLength={8}
                      required
                    />
                  </div>
                  <div className="flex justify-end">
                    <button
                      type="submit"
                      className="bg-accent-gradient text-on-accent px-8 py-3 rounded-2xl font-bold text-xs uppercase tracking-widest hover:opacity-90 transition-colors disabled:opacity-50"
                      disabled={passwordPending}
                    >
                      {passwordPending ? t('website.profile.updating') : t('website.profile.updateCredentials')}
                    </button>
                  </div>
               </form>
             </div>
          </div>
        )}

        <div className="bg-primary rounded-4xl border border-border overflow-hidden shadow-sm">
          <div className="px-6 py-4 border-b border-border bg-secondary/20 flex items-center justify-between">
            <div>
              <h3 className="text-xs font-bold uppercase tracking-[0.2em] mb-0.5">{t('addresses.title')}</h3>
              <p className="text-[10px] text-text-secondary uppercase tracking-wider">{t('website.profile.addressesSubtitle')}</p>
            </div>
            <Icon name="home_pin" className="text-text-secondary opacity-30 text-xl" />
          </div>
          <div className="p-6 grid gap-4">
            <button
              type="button"
              className="border border-border-solid rounded-2xl px-4 py-2.5 bg-secondary/30 text-text font-bold text-xs uppercase tracking-widest cursor-pointer hover:bg-secondary/50"
              onClick={openCreateAddressForm}
              disabled={addressPending}
            >
              {t('addresses.addNew')}
            </button>

            {addressFormOpen ? (
              <form className="grid gap-3 border border-border/60 rounded-2xl p-4" onSubmit={(event) => void handleSubmitAddress(event)}>
                <input
                  type="text"
                  className="w-full border border-border-solid rounded-2xl px-4 py-2.5 bg-secondary/30 text-text placeholder:text-text-secondary/50 focus:outline-none focus:ring-2 focus:ring-accent/20"
                  value={addressLabel}
                  onChange={(event) => setAddressLabel(event.target.value)}
                  placeholder={t('addresses.labelPlaceholder')}
                />
                <PhoneInput
                  country="ge"
                  preferredCountries={['ge', 'tr', 'by', 'ua', 'ru']}
                  enableSearch
                  value={addressPhone}
                  onChange={(value, _country, _event, formattedValue) => {
                    const normalizedPhone = formattedValue?.trim() || (value ? `+${value}` : '');
                    setAddressPhone(normalizedPhone);
                  }}
                  placeholder={t('cart.phonePlaceholder')}
                  specialLabel=""
                  containerClass="!w-full"
                  inputClass="!w-full !h-[44px] !bg-secondary/30 !border !border-border-solid !rounded-2xl !pl-[72px] !pr-4 !text-text !text-base"
                  buttonClass="!border-none !bg-transparent !rounded-l-2xl !w-14"
                  dropdownClass="!w-full !max-w-full !min-w-[160px] !mt-2 !border-none !rounded-4xl !bg-primary !text-text !shadow-ambient"
                  searchClass="!bg-primary"
                />
                <AddressAutocomplete
                  id="website-profile-address"
                  value={addressValue}
                  onChange={setAddressValue}
                  onLocationChange={setAddressLocation}
                  placeholder={t('cart.deliveryPlaceholder')}
                />
                <div className="flex gap-2 justify-end">
                  <button
                    type="button"
                    className="bg-secondary text-text px-4 py-2 rounded-2xl font-bold text-xs uppercase tracking-widest border-none cursor-pointer"
                    onClick={() => {
                      setAddressFormOpen(false);
                      resetAddressForm();
                    }}
                    disabled={addressPending}
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    type="submit"
                    className="bg-accent-gradient text-on-accent px-4 py-2 rounded-2xl font-bold text-xs uppercase tracking-widest border-none cursor-pointer disabled:opacity-60"
                    disabled={addressPending}
                  >
                    {addressPending ? t('website.profile.saving') : t('mapModal.save')}
                  </button>
                </div>
              </form>
            ) : null}

            {addressesLoading ? (
              <p className="m-0 text-xs text-text-secondary uppercase tracking-wider">{t('website.profile.loadingAddresses')}</p>
            ) : addresses.length === 0 ? (
              <p className="m-0 text-xs text-text-secondary uppercase tracking-wider">{t('website.profile.noSavedAddresses')}</p>
            ) : (
              <div className="grid gap-3">
                {addresses.map((address) => (
                  <article key={address.id} className="border border-border/60 rounded-2xl p-4 grid gap-3 bg-secondary/20">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="m-0 text-sm font-semibold">{address.label || address.address}</p>
                        <p className="m-0 mt-1 text-xs text-text-secondary">{address.address}</p>
                        {address.phoneNumber ? (
                          <p className="m-0 mt-1 text-xs text-text-secondary">{address.phoneNumber}</p>
                        ) : null}
                      </div>
                      {address.isDefault ? (
                        <span className="text-[10px] font-bold uppercase tracking-widest text-success">★ {t('addresses.defaultBadge')}</span>
                      ) : (
                        <button
                          type="button"
                          className="text-[10px] font-bold uppercase tracking-widest text-accent bg-transparent border-none p-0 cursor-pointer"
                          onClick={() => void handleSetDefaultAddress(address)}
                          disabled={addressPending}
                        >
                          {t('addresses.setDefault')}
                        </button>
                      )}
                    </div>

                    {!address.location ? (
                      <button
                        type="button"
                        className="text-xs text-error bg-transparent border-none p-0 text-left cursor-pointer"
                        onClick={() => setMapTargetAddressId(address.id)}
                        disabled={addressPending}
                      >
                        {t('addresses.noPreciseLocation')} {t('addresses.tapToPin')}
                      </button>
                    ) : null}

                    <div className="flex gap-2 justify-end">
                      <button
                        type="button"
                        className="bg-secondary text-text px-4 py-2 rounded-2xl font-bold text-xs uppercase tracking-widest border-none cursor-pointer"
                        onClick={() => openEditAddressForm(address)}
                        disabled={addressPending}
                      >
                        {t('common.edit')}
                      </button>
                      <button
                        type="button"
                        className="bg-error-container text-on-error-container px-4 py-2 rounded-2xl font-bold text-xs uppercase tracking-widest border-none cursor-pointer"
                        onClick={() => void handleDeleteAddress(address)}
                        disabled={addressPending}
                      >
                        {t('common.delete')}
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Session Management */}
        <div className="bg-primary rounded-4xl border border-border overflow-hidden shadow-sm">
           <div className="px-6 py-4 border-b border-border bg-secondary/20 flex items-center justify-between">
              <div>
                <h3 className="text-xs font-bold uppercase tracking-[0.2em] mb-0.5">{t('website.profile.sessionTitle')}</h3>
                <p className="text-[10px] text-text-secondary uppercase tracking-wider">{t('website.profile.sessionSubtitle')}</p>
              </div>
              <Icon name="logout" className="text-text-secondary opacity-30 text-xl" />
           </div>
           <div className="p-6">
             <button 
               onClick={() => void logout()} 
               className="flex items-center gap-2 text-error font-bold uppercase tracking-[0.2em] text-xs hover:opacity-80 transition-opacity"
             >
               <Icon name="logout" className="text-lg" />
               {t('website.header.logout')}
             </button>
           </div>
        </div>

        {message && (
          <div className="bg-accent-soft border border-accent/20 rounded-2xl p-4 text-on-accent-soft text-sm text-center font-medium animate-in fade-in duration-500">
            {message}
          </div>
        )}
      </div>
    </section>
    <BrowserOnly reason="Google Maps JS API is browser-only" fallback={<div aria-hidden="true" className="min-h-[300px]" />}>
    <AddressMapModal
      key={`${mapTargetAddressId ?? 'none'}:${mapTargetAddress?.location?.lat ?? 'na'}:${mapTargetAddress?.location?.lon ?? 'na'}`}
      open={mapTargetAddress !== null}
      initialLocation={mapTargetAddress?.location ?? addressLocation}
      addressHint={mapTargetAddress?.address ?? ''}
      apiKey={appConfig?.googleMapsBrowserApiKey ?? ''}
      title={t('mapModal.title')}
      subtitle={t('mapModal.subtitle')}
      saveLabel={t('mapModal.save')}
      cancelLabel={t('mapModal.cancel')}
      outsideDeliveryZoneMessage={t('mapModal.outsideDeliveryZone')}
      onSave={(location) => void handleSaveAddressLocation(location)}
      onClose={() => setMapTargetAddressId(null)}
    />
    </BrowserOnly>
    </>
  );
}
